// V1.5+ Slice 7 — Fortify Relay tuning per docs/02_GAME_DESIGN.md §13 / §15 / §16.
//
// Fortify Relay is the Shield's E (secondary) ability — the FIRST defensive
// primitive in the V1.5+ network arc. It is the dual of the disruption
// triangle established by Slices 2 / 5 / 6:
//
//   Cut Link    (Recon E)    — HAMMER : binary chain destruction. Attacker.
//   Jam Relay   (Engineer E) — SCALPEL: binary vision suppression. Attacker.
//   Disrupt     (Assault E)  — DIAL   : analog turret degradation. Attacker.
//   Uplink
//   Fortify     (Shield E)   — DEFENSE: counter-window for the relay owner.
//   Relay
//
// Mechanic: while a relay is in a fortify window, two things happen:
//   1. Incoming cut_link / jam_relay / disrupt_uplink APPLY is a no-op on the
//      relay (the channeling enemy still pays the cooldown — fortify is a
//      counter-WINDOW, not a deflection of the channel itself, so it creates
//      §15 action: defender must commit to channeling Fortify before the
//      attacker channels). The channel completes, but the apply does nothing.
//   2. Forward `mini_turret`s anchored on the fortified relay (within
//      FORWARD_TURRET_RANGE_PX, owned by the fortifier) get +25% fire rate
//      via FORTIFY_RELAY_TURRET_FIRE_RATE_MULT. Mathematical mirror of
//      Disrupt Uplink's 0.6× — together they span the [0.6, 1.25] turret
//      fire-rate band.
//
// Why Shield E is defensive, not a 4th disruption variant:
//   - Slices 2 / 5 / 6 are all attacker tools. The §16 "Care: do not
//     weaponize frustration" rule and §13 counterplay rules need a
//     defender's primitive before stacking a 4th attacker tool.
//   - Shield's identity per docs/02_GAME_DESIGN.md §2 is "tank, frontline
//     anchor" — slow, low DPS, high HP. A defender's E maps to that
//     identity ("hold this position"); an attacker's would not.
//   - The mechanical novelty per docs/11_ROADMAP.md Phase 6+ row is "first
//     defensive disruption primitive" — the dual of the existing arc.
//
// Calibration:
//   - Range `FORTIFY_RELAY_TARGET_RANGE_PX` 200 px — Shield is the slowest
//     archetype (speed 70). Tighter commit than Cut Link's 220 / Disrupt's
//     240 because Shield can't disengage; the closer commit is on-shape.
//     Same as Jam Relay's 200 (Engineer is also relatively static).
//   - Channel `FORTIFY_RELAY_CHANNEL_SEC` 2.5 s — between Engineer's 2 and
//     Recon's 3. Shield is tankier than both, but its low mobility means a
//     longer channel is a death sentence. Mid is right.
//   - Window `FORTIFY_RELAY_DURATION_SEC` 20 s — equal to Jam Relay's 20.
//     Defensive abilities should not be longer than offensive ones; matched
//     to the shortest existing window so the §17 anti-turtling gate has
//     headroom.
//   - Cooldown `FORTIFY_RELAY_COOLDOWN_SEC` 50 s — LONGEST in the arc (35 /
//     40 / 45 for jam / disrupt / cut). Defensive uptime ≤ 40% (20/50);
//     beyond that turtling-lite emerges. Conservative tuning per Rule 17 —
//     better to under-deliver and bump the duration up later than ship a
//     §17 regression.
//   - `FORTIFY_RELAY_TURRET_FIRE_RATE_MULT` 1.25 — exact mirror of Disrupt
//     Uplink's 0.6× (1/0.8 = 1.25; 1/1.25 = 0.8 cooldown reset). The pair
//     spans the [0.6, 1.25] turret fire-rate band.
//
// §17 anti-turtling gate (the rule this slice must NOT regress):
//   Pre-Slice-7 baseline: averageRelayFlipsPerMatch = 7.0 (per Slice 6
//   ship). A defensive ability could push this DOWN — that would be a
//   §17 fail. Verify with `playtest-direct.mjs 30` post-merge: flip rate
//   must be ≥ 6.4 (10% headroom from baseline). If it drops, tune
//   FORTIFY_RELAY_DURATION_SEC down (20→15) or FORTIFY_RELAY_COOLDOWN_SEC
//   up (50→60) and retry.
//
// Don't tune these without a corresponding playtest verification.
export const FORTIFY_RELAY_TARGET_RANGE_PX = 200;
export const FORTIFY_RELAY_TARGET_RANGE_SQ =
  FORTIFY_RELAY_TARGET_RANGE_PX * FORTIFY_RELAY_TARGET_RANGE_PX;
export const FORTIFY_RELAY_CHANNEL_SEC = 2.5;
export const FORTIFY_RELAY_DURATION_SEC = 20;
export const FORTIFY_RELAY_COOLDOWN_SEC = 50;
export const FORTIFY_RELAY_TURRET_FIRE_RATE_MULT = 1.25;
